import React from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Event } from "@uno/protocol";
import { AppContext } from "context";
import Avatar from "component/Ui/Avatar";
import useStoreRoomChat from "store/useStoreRoomChat";
import useStorePlayer from "store/useStorePlayer";

const MAX_LENGTH = 60;

const sanitize = (raw: string): string => raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_LENGTH);

const ChatDrawer = () => {
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const { roomId } = useParams<{ roomId: string }>();
    const messages = useStoreRoomChat((state) => state.messages);
    const isOpen = useStoreRoomChat((state) => state.isOpen);
    const close = useStoreRoomChat((state) => state.close);
    const triggerCooldown = useStoreRoomChat((state) => state.triggerCooldown);
    const inCooldown = useStoreRoomChat((state) => state.inCooldown);
    const myPlayerId = useStorePlayer((state) => state.player?.id);
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [value, setValue] = React.useState("");
    const lastTypingEmit = React.useRef(0);

    React.useEffect(() => {
        if(!isOpen) {
            return;
        }

        setValue("");
        const frame = requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
            inputRef.current?.focus();
        });

        return () => cancelAnimationFrame(frame);
    }, [isOpen]);

    const [viewportOffset, setViewportOffset] = React.useState({ top: 0, height: 0 });

    React.useEffect(() => {
        if(!isOpen) {
            return;
        }

        const update = () => {
            const viewport = window.visualViewport;

            if(viewport) {
                setViewportOffset({ top: viewport.offsetTop, height: viewport.height });
            }
        };

        update();
        window.visualViewport?.addEventListener("resize", update);
        window.visualViewport?.addEventListener("scroll", update);

        return () => {
            window.visualViewport?.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("scroll", update);
        };
    }, [isOpen]);

    React.useEffect(() => {
        if(!isOpen || !scrollRef.current) {
            return;
        }

        const frame = requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        });

        return () => cancelAnimationFrame(frame);
    }, [messages, isOpen]);

    const handleChange = (text: string) => {
        setValue(text);

        const now = Date.now();

        if(text && socket && roomId && now - lastTypingEmit.current > 2000) {
            lastTypingEmit.current = now;
            socket.volatile.emit(Event.RoomChatTyping, roomId);
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const text = sanitize(value);

        if(!text || !socket || !roomId) {
            return;
        }

        socket.emit(Event.RoomChatMessage, roomId, text);
        triggerCooldown();
        setValue("");
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/30 z-[70] md:hidden"
                        onClick={close}
                    />
                    <motion.div
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ type: "spring", stiffness: 350, damping: 35 }}
                        className="fixed left-0 w-80 md:w-96 bg-black/40 backdrop-blur-xl border-r border-white/10 z-[71] flex flex-col overflow-hidden transition-[top,height] duration-300 ease-out"
                        style={viewportOffset.height > 0
                            ? { top: viewportOffset.top, height: viewportOffset.height }
                            : { top: 0, bottom: 0 }
                        }
                    >
                        {/* Header */}
                        <div
                            className="flex items-center gap-3 px-5 py-4 border-b border-white/10"
                            style={{ paddingTop: "calc(var(--safe-top) + 16px)" }}
                        >
                            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white/70" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 3C6.477 3 2 6.805 2 11.5c0 2.31 1.086 4.402 2.85 5.932L4 21l4.4-1.95c1.12.29 2.32.45 3.6.45 5.523 0 10-3.805 10-8.5S17.523 3 12 3z" />
                                </svg>
                            </div>
                            <span className="flex-1 text-white font-changa-one text-sm tracking-wide">{t("room.chat_title")}</span>
                            <button onClick={close} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors cursor-pointer">
                                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Messages */}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto panel-scrollbar px-4 py-4 flex flex-col gap-3"
                        >
                            {messages.length === 0 && (
                                <div className="flex-1 flex flex-col items-center justify-center gap-3 opacity-30">
                                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 3C6.477 3 2 6.805 2 11.5c0 2.31 1.086 4.402 2.85 5.932L4 21l4.4-1.95c1.12.29 2.32.45 3.6.45 5.523 0 10-3.805 10-8.5S17.523 3 12 3z" />
                                    </svg>
                                    <p className="text-white text-xs text-center">{t("room.chat_empty")}</p>
                                </div>
                            )}
                            {messages.map((message, index) => {
                                const previous = messages[index - 1];
                                const isContinuation = previous && previous.playerId === message.playerId;
                                const isMine = message.playerId === myPlayerId;

                                return (
                                    <motion.div
                                        key={message.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.15 }}
                                        className={`flex items-end gap-2.5 max-w-full ${isMine ? "flex-row-reverse" : ""} ${isContinuation ? "-mt-1.5" : ""}`}
                                    >
                                        {isContinuation ? (
                                            <div className="w-7 shrink-0" />
                                        ) : (
                                            <Avatar src={message.avatar} className="w-7 h-7 shrink-0 ring-1 ring-white/20" />
                                        )}
                                        <div className={`flex flex-col gap-0.5 min-w-0 max-w-[80%] ${isMine ? "items-end" : "items-start"}`}>
                                            {!isContinuation && !isMine && (
                                                <span className="text-white/50 text-[9px] font-bold uppercase tracking-wider leading-none ml-1">{message.username}</span>
                                            )}
                                            <div className={`rounded-2xl px-3.5 py-1.5 text-[13px] break-words leading-snug shadow-sm flex items-end gap-2 ${
                                                isMine
                                                    ? "bg-white/90 text-gray-900"
                                                    : "bg-white/10 text-white/90"
                                            }`}>
                                                <span className="flex-1">{message.text}</span>
                                                <span className={`text-[9px] shrink-0 leading-none pb-0.5 ${isMine ? "text-gray-400" : "text-white/30"}`}>
                                                    {new Date(message.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Input */}
                        <form
                            onSubmit={handleSubmit}
                            className="shrink-0 px-4 py-3 border-t border-white/10"
                            style={{ paddingBottom: "max(12px, var(--safe-bottom))" }}
                        >
                            <div className="flex items-center gap-2 bg-white/8 rounded-2xl px-1 py-1 border border-white/10">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    inputMode="text"
                                    autoComplete="off"
                                    autoCorrect="on"
                                    autoCapitalize="sentences"
                                    spellCheck
                                    value={value}
                                    onChange={(event) => handleChange(event.target.value.slice(0, MAX_LENGTH))}
                                    placeholder={t("room.chat_placeholder")}
                                    maxLength={MAX_LENGTH}
                                    className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none px-3 py-1.5"
                                />
                                <button
                                    type="submit"
                                    disabled={!value.trim() || inCooldown}
                                    className="shrink-0 w-8 h-8 rounded-xl bg-white/15 hover:bg-white/25 disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer transition-colors"
                                >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3 20l18-8L3 4l3 8zm0 0h12" />
                                    </svg>
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body,
    );
};

export default ChatDrawer;
